// ============================================================
// supabaseDb - ASYNC Supabase-based data layer
// Tüm fonksiyonlar { data, error } formatında döner
// localStorageDb ile aynı API yüzeyini sunar
// ============================================================

import { supabase } from './supabase';

// ============================================================
// HELPERS
// ============================================================

type DbResult<T> = { data: T | null; error: string | null; deletedCustomer?: any | null; errorCode?: string | null; debugInfo?: any | null };

let cachedBusinessId: string | null = null;

/**
 * Telefon numarasını normalize eder: rakam olmayanları siler, +90 ve baştaki 0'ı kaldırır.
 * Örn:
 *   "0530 123 45 67" → "5301234567"
 *   "+90 530 123 45 67" → "5301234567"
 *   "905301234567" → "5301234567"
 */
export function normalizePhone(phone: string): string {
  if (!phone) return '';
  let cleaned = phone.replace(/[^0-9]/g, '');
  // +90 prefix: 905XXXXXXXXX → 5XXXXXXXXX (en az 12 hane)
  if (cleaned.startsWith('90') && cleaned.length >= 12) {
    cleaned = cleaned.substring(2);
  }
  // 0 prefix: 05XXXXXXXXX → 5XXXXXXXXX
  if (cleaned.startsWith('0')) {
    cleaned = cleaned.substring(1);
  }
  return cleaned;
}

export async function getUserBusinessId(): Promise<string | null> {
  if (cachedBusinessId) return cachedBusinessId;
  if (!supabase || !supabase.auth) return null;

  try {
    const result = await supabase.auth.getSession();
    const session = result?.data?.session;
    if (!session?.user?.id) return null;

    // .maybeSingle() burada kullanılmaz: kullanıcı ileride birden fazla
    // işletmeye üye olabilir, bu durumda .maybeSingle() "multiple rows"
    // hatası verip null döner ve tüm veri erişimini kilitlerdi. Şimdilik
    // (çoklu işletme arası geçiş UI'ı henüz yok) en eski üyeliğe girilir.
    const { data, error } = await supabase
      .from('business_members')
      .select('business_id')
      .eq('user_id', session.user.id)
      .eq('status', 'Aktif')
      .order('created_at', { ascending: true })
      .limit(1);

    if (error || !data || data.length === 0) return null;
    cachedBusinessId = data[0].business_id;
    return cachedBusinessId;
  } catch {
    return null;
  }
}

export function clearBusinessIdCache(): void {
  cachedBusinessId = null;
}

function handleError(err: any): string {
  if (!err) return 'Bilinmeyen hata';
  return err.message || err.details || String(err);
}

function sanitizeItem<T extends Record<string, any>>(item: T): T {
  const cleaned: any = {};
  for (const [key, value] of Object.entries(item)) {
    if (value === undefined) continue;
    cleaned[key] = value;
  }
  return cleaned as T;
}

// ============================================================
// ONBOARDING (yeni işletme kaydı)
// ============================================================

export interface BusinessOnboardingPayload {
  business: {
    name: string;
    type?: string;
    tax_office?: string;
    tax_number?: string;
    authorized_person_name?: string;
    authorized_person_phone?: string;
    authorized_person_email?: string;
    phone?: string;
    email?: string;
    address?: string;
    city?: string;
    district?: string;
    neighborhood?: string;
    logo_url?: string;
    currency?: string;
  };
  package: {
    package_type: 'monthly' | 'yearly';
  };
}

/**
 * Atomik RPC: yeni işletme + admin üyelik + deneme aboneliği + belge
 * sayaçlarını tek transaction'da oluşturur. Çağıran kullanıcının auth
 * oturumu olmalı (önce supabase.auth.signUp/signInWithPassword yapılmış
 * olmalı) ama henüz hiçbir işletmeye üye olmamalı.
 */
export async function createBusinessOnboarding(
  payload: BusinessOnboardingPayload
): Promise<DbResult<{ business_id: string }>> {
  try {
    const { data, error } = await supabase.rpc('create_business_onboarding', { payload });

    if (error) return { data: null, error: handleError(error) };

    const result = data as any;
    if (result && result.success === false) {
      return { data: null, error: result.error || 'İşletme oluşturulamadı' };
    }

    clearBusinessIdCache();
    return { data: result, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

// ============================================================
// GENERIC CRUD
// ============================================================

export async function getAll<T = any>(table: string): Promise<DbResult<T[]>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { data, error } = await supabase
      .from(table)
      .select('*')
      .eq('business_id', bizId)
      .order('created_at', { ascending: false });

    if (error) return { data: null, error: handleError(error) };
    return { data: (data as T[]) || [], error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

export async function getById<T = any>(table: string, id: string): Promise<DbResult<T>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { data, error } = await supabase
      .from(table)
      .select('*')
      .eq('id', id)
      .eq('business_id', bizId)
      .maybeSingle();

    if (error) return { data: null, error: handleError(error) };
    return { data: (data as T) || null, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

export async function create<T = any>(table: string, item: Partial<T> & Record<string, any>): Promise<DbResult<T>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { data, error } = await supabase
      .from(table)
      .insert({ ...sanitizeItem(item), business_id: bizId })
      .select()
      .single();

    if (error) return { data: null, error: handleError(error) };
    return { data: data as T, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

export async function update<T = any>(table: string, id: string, updates: Partial<T> & Record<string, any>): Promise<DbResult<T>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { data, error } = await supabase
      .from(table)
      .update({ ...sanitizeItem(updates), updated_at: new Date().toISOString() })
      .eq('id', id)
      .eq('business_id', bizId)
      .select()
      .single();

    if (error) return { data: null, error: handleError(error) };
    return { data: data as T, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

export async function remove(table: string, id: string): Promise<DbResult<boolean>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { error } = await supabase
      .from(table)
      .delete()
      .eq('id', id)
      .eq('business_id', bizId);

    if (error) return { data: null, error: handleError(error) };
    return { data: true, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

export async function queryEq<T = any>(table: string, column: string, value: any): Promise<DbResult<T[]>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { data, error } = await supabase
      .from(table)
      .select('*')
      .eq('business_id', bizId)
      .eq(column, value)
      .order('created_at', { ascending: false });

    if (error) return { data: null, error: handleError(error) };
    return { data: (data as T[]) || [], error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

export async function queryRange<T = any>(table: string, column: string, gte: string, lte: string): Promise<DbResult<T[]>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { data, error } = await supabase
      .from(table)
      .select('*')
      .eq('business_id', bizId)
      .gte(column, gte)
      .lte(column, lte)
      .order(column, { ascending: false });

    if (error) return { data: null, error: handleError(error) };
    return { data: (data as T[]) || [], error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

// ============================================================
// VIEW HELPERS - localStorage simülasyonu için
// ============================================================

let _localFallback = false;

export function enableLocalFallback(): void {
  _localFallback = true;
}

export function disableLocalFallback(): void {
  _localFallback = false;
}

export function isLocalFallback(): boolean {
  return _localFallback;
}

// ============================================================
// CUSTOMERS
// ============================================================
export async function getCustomers(): Promise<DbResult<any[]>> {
  return getAll('customers');
}

/**
 * ORTAK MÜŞTERİ ARAMA SERVİSİ (Server-side, paginated, Türkçe karakter duyarlı)
 *
 * Tüm modüller (Müşteriler, Ödeme, Ödeme Sözü, Senet, Satış, Teslimat, İcra,
 * Global Arama, CustomerSearchSelect) bu fonksiyonu kullanmalıdır.
 *
 * Veritabanı RPC'si (search_customers_v2) üzerinden turkish_normalize ile
 * çok alanlı arama yapar. Türkçe karakterleri (İ,ı,Ö,ö,Ü,ü,Ş,ş,Ğ,ğ,Ç,ç)
 * doğru şekilde işler.
 *
 * Aranan alanlar: full_name, phone, second_phone, phone_normalized, customer_no,
 * identity_number, district
 *
 * @param query     - Arama terimi (boşsa tüm aktif müşterileri döndürür)
 * @param filters   - Opsiyonel: status ve district filtreleri
 * @param page      - Sayfa numarası (1-bazlı, default: 1)
 * @param pageSize  - Sayfa başına kayıt (default: 8)
 */
export async function searchCustomers(
  query: string,
  filters?: { status?: string; district?: string },
  page: number = 1,
  pageSize: number = 8,
): Promise<DbResult<{ customers: any[]; total: number }>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const trimmed = (query || '').trim();

    console.debug('[searchCustomers] RPC çağrısı:', { query: trimmed, page, pageSize, filters });

    const { data, error } = await supabase.rpc('search_customers_v3', {
      p_business_id: bizId,
      p_query: trimmed,
      p_status_filter: filters?.status || null,
      p_district_filter: filters?.district || null,
      p_page: page,
      p_page_size: pageSize,
      p_include_archived: false,
    });

    if (error) {
      console.error('[searchCustomers] RPC hatası:', { code: (error as any).code, message: error.message, details: (error as any).details });
      return { data: null, error: handleError(error) };
    }

    const result = data as any;
    console.debug('[searchCustomers] RPC sonucu:', { hasResult: !!result, total: result?.total, customerCount: result?.customers?.length });

    if (!result) {
      return { data: { customers: [], total: 0 }, error: null };
    }

    return {
      data: {
        customers: (result.customers || []) as any[],
        total: result.total || 0,
      },
      error: null,
    };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

/**
 * Müşteri seçici (dropdown) için sunucu tarafı arama.
 * RPC üzerinden turkish_normalize ile Türkçe karakter duyarlı arama yapar.
 * İlk 100 sonucu döndürür.
 */
export async function searchCustomersForSelect(query: string): Promise<DbResult<any[]>> {
  const result = await searchCustomers(query, undefined, 1, 100);
  if (result.error) return { data: null, error: result.error };
  return { data: result.data?.customers || [], error: null };
}

export async function getCustomerById(id: string): Promise<DbResult<any>> {
  return getById('customers', id);
}

/**
 * Telefon numarasına göre müşteri var mı kontrol eder (normalize edilmiş).
 * Varsa mevcut müşteriyi döndürür, yoksa null.
 */
/**
 * Telefon numarasına göre AKTİF müşteri var mı kontrol eder (normalize edilmiş).
 * Silinmiş/arşivlenmiş müşteriler dikkate alınmaz.
 * Varsa mevcut müşteriyi döndürür, yoksa null.
 */
export async function checkCustomerExistsByPhone(phone: string): Promise<DbResult<any | null>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const normalized = normalizePhone(phone);
    if (!normalized) return { data: null, error: null }; // boş telefon kontrol edilmez

    const { data, error } = await supabase
      .from('customers')
      .select('id, full_name, phone, customer_no, status')
      .eq('business_id', bizId)
      .eq('phone_normalized', normalized)
      .not('status', 'in', '("Silinmiş","Arşivlenmiş")')
      .maybeSingle();

    if (error) return { data: null, error: handleError(error) };
    return { data: data || null, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

/**
 * Müşteri oluşturma — artık her zaman atomik RPC create_customer_transaction kullanır.
 * Bu sayede:
 * - Tüm validasyon ve mükerrer kontrolü server-side yapılır
 * - Telefon normalizasyonu server-side yapılır
 * - customer_no otomatik oluşturulur
 * - Hata kodları doğru constraint tespiti ile döner
 * - Race condition riski yoktur (tek transaction)
 */
export async function createCustomer(data: any): Promise<DbResult<any>> {
  // Direkt olarak atomic RPC'ye yönlendir — tüm yollar aynı kapıdan geçsin
  return createCustomerAtomic(data);
}

/**
 * Atomik müşteri oluşturma — tek RPC çağrısıyla:
 * - Server-side doğrulama
 * - Telefon mükerrer kontrolü (sadece aktif müşteriler)
 * - Silinmiş müşteri tespiti (DELETED_PHONE_EXISTS)
 * - Müşteri no oluşturma
 * - Insert (hepsi ya da hiçbiri)
 *
 * @param data - Müşteri bilgileri
 * @param forceCreate - true ise silinmiş müşteri engelini yoksay
 */
export async function createCustomerAtomic(
  data: {
    full_name: string;
    phone: string;
    customer_no?: string;
    second_phone?: string;
    address?: string;
    city?: string;
    district?: string;
    neighborhood?: string;
    identity_number?: string;
    notes?: string;
    status?: string;
  },
  forceCreate: boolean = false,
): Promise<DbResult<any>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { neighborhood, ...rpcData } = data;
    const { data: result, error } = await supabase.rpc('create_customer_transaction', {
      payload: { ...rpcData, business_id: bizId, force_create: forceCreate },
    });

    if (error) return { data: null, error: handleError(error) };

    const res = result as any;
    if (!res || res.success === false) {
      // DELETED_PHONE_EXISTS: özel durum, deleted_customer bilgisini de döndür
      if (res && res.code === 'DELETED_PHONE_EXISTS') {
        return {
          data: null,
          error: res.error,
          errorCode: res.code,
          debugInfo: res.debug || null,
          deletedCustomer: res.deleted_customer || null,
        };
      }
      return {
        data: null,
        error: (res && res.error) || 'Müşteri oluşturulamadı.',
        errorCode: (res && res.code) || 'UNKNOWN',
        debugInfo: (res && res.debug) || null,
      };
    }

    let customer = res.customer;
    // create_customer_transaction RPC'nin sabit kolon listesi neighborhood'u
    // henüz bilmiyor (bu RPC'nin tam kaynağı bu repodaki migration'larda
    // bulunmuyor, körlemesine değiştirmek riskli) — bu yüzden mahalle,
    // müşteri oluşturulduktan sonra ayrı, düz bir update() ile yazılır.
    if (neighborhood && customer?.id) {
      const patchRes = await update('customers', customer.id, { neighborhood });
      if (patchRes.data) customer = patchRes.data;
    }

    return { data: customer, error: null, debugInfo: res.debug || null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

/**
 * Silinmiş bir müşteriyi geri yükler (status'ü Aktif yapar, telefon normalize'ini düzeltir).
 */
export async function restoreDeletedCustomer(customerId: string): Promise<DbResult<any>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    // Önce mevcut müşteriyi al
    const { data: existing } = await supabase
      .from('customers')
      .select('phone_normalized, phone')
      .eq('id', customerId)
      .eq('business_id', bizId)
      .maybeSingle();

    if (!existing) return { data: null, error: 'Müşteri bulunamadı.' };

    // Telefon normalize'ini düzelt: _deleted_TIMESTAMP suffix'ini kaldır
    const fixedPhoneNormalized = ((existing as any).phone_normalized || '').replace(/_deleted_\d+$/, '');
    const fixedPhone = ((existing as any).phone || '').replace(/\s*\(Silinmiş\)$/, '');

    const { data, error } = await supabase
      .from('customers')
      .update({
        status: 'Aktif',
        phone_normalized: fixedPhoneNormalized,
        phone: fixedPhone,
        updated_at: new Date().toISOString(),
      })
      .eq('id', customerId)
      .eq('business_id', bizId)
      .select()
      .single();

    if (error) return { data: null, error: handleError(error) };
    return { data, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

export async function updateCustomer(id: string, data: any): Promise<DbResult<any>> {
  return update('customers', id, data);
}

/**
 * Müşteri silme — tek atomik RPC delete_customer_transaction kullanır.
 * Önceki sürüm birden çok bağımsız supabase-js çağrısıyla parça parça
 * siliyordu: yanlış sırada (ör. promissory_notes, sales'tan SONRA
 * siliniyordu) ve payment_allocations/payment_promise_allocations/
 * promissory_note_debt_allocations/promissory_note_movements gibi bazı
 * tablolara hiç dokunmuyordu — bu da "payments silinemedi:
 * promissory_note_payment_allocations_payment_id_fkey" gibi foreign key
 * hatalarına ve (bir adım ortada patlarsa) müşterinin yarım silinmiş
 * kalmasına yol açıyordu. RPC, tüm bağlı kayıtları doğru sırayla tek
 * transaction içinde siler; herhangi bir adım başarısız olursa tüm
 * değişiklikler otomatik geri alınır.
 */
export async function deleteCustomer(id: string): Promise<DbResult<boolean>> {
  try {
    const { data, error } = await supabase.rpc('delete_customer_transaction', {
      p_customer_id: id,
    });
    if (error) return { data: null, error: handleError(error) };
    const res = data as any;
    if (!res?.success) return { data: null, error: res?.error || 'Müşteri silinemedi' };
    return { data: true, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

// ============================================================
// PRODUCTS
// ============================================================
export async function getProducts(): Promise<DbResult<any[]>> {
  return getAll('products');
}

export async function getProductById(id: string): Promise<DbResult<any>> {
  return getById('products', id);
}

export async function createProduct(data: any): Promise<DbResult<any>> {
  return create('products', data);
}

export async function updateProduct(id: string, data: any): Promise<DbResult<any>> {
  return update('products', id, data);
}

export async function deleteProduct(id: string): Promise<DbResult<boolean>> {
  return remove('products', id);
}

// ============================================================
// PROFILO PRODUCT SEARCH (Model kodu ile arama)
// ============================================================

export interface ProfiloProductResult {
  id: string;
  model_code: string;
  name: string;
  category: string;
  product_type: string;
  brand: string;
  color: string;
  size: string;
  capacity: string;
  energy_class: string;
  stock_quantity: number;
  sale_price: number;
  purchase_price: number;
  description: string;
  status: string;
  warranty: string;
}

/**
 * Profilo ürünlerini model koduna göre arar.
 * Model kodunu normalize eder (büyük harf, boşluk/tire sil) ve LIKE ile eşleştirir.
 * Tam eşleşme en üstte, sonra isim sırasına göre döner. En fazla 20 sonuç.
 */
export async function searchProfiloProductsByModelCode(modelCode: string): Promise<DbResult<ProfiloProductResult[]>> {
  try {
    const trimmed = (modelCode || '').trim();
    if (trimmed.length < 2) return { data: [], error: null };

    const { data, error } = await supabase.rpc('search_profilo_products_by_model_code', {
      p_model_code: trimmed,
    });

    if (error) return { data: null, error: handleError(error) };
    return { data: (data as ProfiloProductResult[]) || [], error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

// ============================================================
// SALES
// ============================================================
export async function getSales(): Promise<DbResult<any[]>> {
  return getAll('sales');
}

export async function getSaleById(id: string): Promise<DbResult<any>> {
  return getById('sales', id);
}

export async function getSalesByCustomerId(customerId: string): Promise<DbResult<any[]>> {
  return queryEq('sales', 'customer_id', customerId);
}

export async function createSale(data: any): Promise<DbResult<any>> {
  return create('sales', data);
}

export async function updateSale(id: string, data: any): Promise<DbResult<any>> {
  return update('sales', id, data);
}

export async function deleteSale(id: string): Promise<DbResult<boolean>> {
  return remove('sales', id);
}

// ============================================================
// SALE ITEMS
// ============================================================
export async function getSaleItems(saleId: string): Promise<DbResult<any[]>> {
  return queryEq('sale_items', 'sale_id', saleId);
}

export async function getAllSaleItems(): Promise<DbResult<any[]>> {
  return getAll('sale_items');
}

export async function createSaleItem(data: any): Promise<DbResult<any>> {
  return create('sale_items', data);
}

export async function createSaleItems(items: any[]): Promise<DbResult<any[]>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const withBiz = items.map(item => ({ ...sanitizeItem(item), business_id: bizId }));
    const { data, error } = await supabase.from('sale_items').insert(withBiz).select();

    if (error) return { data: null, error: handleError(error) };
    return { data: data as any[], error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

// ============================================================
// PAYMENTS
// ============================================================
export async function getPayments(): Promise<DbResult<any[]>> {
  return getAll('payments');
}

export async function getPaymentsByCustomerId(customerId: string): Promise<DbResult<any[]>> {
  return queryEq('payments', 'customer_id', customerId);
}

export async function getPaymentsByDate(date: string): Promise<DbResult<any[]>> {
  return queryEq('payments', 'payment_date', date);
}

export async function getPaymentsByMonth(gte: string, lte: string): Promise<DbResult<any[]>> {
  return queryRange('payments', 'payment_date', gte, lte);
}

export async function createPayment(data: any): Promise<DbResult<any>> {
  return create('payments', data);
}

export async function deletePayment(id: string): Promise<DbResult<boolean>> {
  return remove('payments', id);
}

// ============================================================
// INSTALLMENTS
// ============================================================
export async function getInstallments(): Promise<DbResult<any[]>> {
  return getAll('installments');
}

export async function getInstallmentsBySaleId(saleId: string): Promise<DbResult<any[]>> {
  return queryEq('installments', 'sale_id', saleId);
}

export async function getOverdueInstallments(): Promise<DbResult<any[]>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { data, error } = await supabase
      .from('installments')
      .select('*')
      .eq('business_id', bizId)
      .eq('status', 'Gecikti')
      .order('due_date', { ascending: true });

    if (error) return { data: null, error: handleError(error) };
    return { data: (data as any[]) || [], error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

export async function createInstallment(data: any): Promise<DbResult<any>> {
  return create('installments', data);
}

export async function createInstallments(items: any[]): Promise<DbResult<any[]>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const withBiz = items.map(item => ({ ...sanitizeItem(item), business_id: bizId }));
    const { data, error } = await supabase.from('installments').insert(withBiz).select();

    if (error) return { data: null, error: handleError(error) };
    return { data: data as any[], error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

// ============================================================
// DELIVERIES
// ============================================================
export async function getDeliveries(): Promise<DbResult<any[]>> {
  return getAll('deliveries');
}

export async function getDeliveriesByCustomerId(customerId: string): Promise<DbResult<any[]>> {
  return queryEq('deliveries', 'customer_id', customerId);
}

export async function getDeliveryBySaleId(saleId: string): Promise<DbResult<any>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { data, error } = await supabase
      .from('deliveries')
      .select('*')
      .eq('business_id', bizId)
      .eq('sale_id', saleId)
      .maybeSingle();

    if (error) return { data: null, error: handleError(error) };
    return { data: data as any, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

export async function getDeliveriesByDate(date: string): Promise<DbResult<any[]>> {
  return queryEq('deliveries', 'delivery_date', date);
}

export async function createDelivery(data: any): Promise<DbResult<any>> {
  return create('deliveries', data);
}

export async function updateDelivery(id: string, data: any): Promise<DbResult<any>> {
  return update('deliveries', id, data);
}

export async function deleteDelivery(id: string): Promise<DbResult<boolean>> {
  return remove('deliveries', id);
}

// ============================================================
// EXPENSES
// ============================================================
export async function getExpenses(): Promise<DbResult<any[]>> {
  return getAll('expenses');
}

export async function createExpense(data: any): Promise<DbResult<any>> {
  return create('expenses', data);
}

export async function deleteExpense(id: string): Promise<DbResult<boolean>> {
  return remove('expenses', id);
}

// ============================================================
// CASH MOVEMENTS
// ============================================================
export async function getCashMovements(): Promise<DbResult<any[]>> {
  return getAll('cash_movements');
}

export async function createCashMovement(data: any): Promise<DbResult<any>> {
  return create('cash_movements', data);
}

// ============================================================
// STOCK MOVEMENTS
// ============================================================
export async function getStockMovements(): Promise<DbResult<any[]>> {
  return getAll('stock_movements');
}

export async function getStockMovementsByProductId(productId: string): Promise<DbResult<any[]>> {
  return queryEq('stock_movements', 'product_id', productId);
}

export async function createStockMovement(data: any): Promise<DbResult<any>> {
  return create('stock_movements', data);
}

// ============================================================
// PROMISSORY NOTES (Senetler)
// ============================================================
export async function getPromissoryNotes(): Promise<DbResult<any[]>> {
  return getAll('promissory_notes');
}

export async function getPromissoryNoteById(id: string): Promise<DbResult<any>> {
  return getById('promissory_notes', id);
}

export async function getPromissoryNotesByCustomerId(customerId: string): Promise<DbResult<any[]>> {
  return queryEq('promissory_notes', 'customer_id', customerId);
}

export async function getPromissoryNotesBySaleId(saleId: string): Promise<DbResult<any[]>> {
  return queryEq('promissory_notes', 'sale_id', saleId);
}

export async function createPromissoryNote(data: any): Promise<DbResult<any>> {
  const principalAmount = data.amount || 0;
  const paidAmount = data.paid_amount || 0;
  const remainingAmount = data.remaining_amount !== undefined ? data.remaining_amount : (principalAmount - paidAmount);

  let autoStatus = data.status || 'Bekliyor';
  if (data.status !== 'İptal') {
    const today = new Date().toISOString().split('T')[0];
    if (remainingAmount <= 0) autoStatus = 'Ödendi';
    else if (data.due_date && data.due_date < today) autoStatus = 'Gecikti';
  }

  return create('promissory_notes', {
    ...data,
    paid_amount: paidAmount,
    remaining_amount: remainingAmount,
    source_type: data.source_type || (data.sale_id ? 'linked_sale' : 'legacy_opening_balance'),
    status: autoStatus,
  });
}

export async function updatePromissoryNote(id: string, data: any): Promise<DbResult<any>> {
  const { data: existing, error: fetchErr } = await getPromissoryNoteById(id);
  if (fetchErr || !existing) return { data: null, error: fetchErr || 'Senet bulunamadı' };

  const principalAmount = data.amount !== undefined ? data.amount : (existing as any).amount || 0;
  const paidAmount = data.paid_amount !== undefined ? data.paid_amount : (existing as any).paid_amount || 0;
  const remainingAmount = principalAmount - paidAmount;

  let autoStatus = data.status !== undefined ? data.status : (existing as any).status;
  if (autoStatus !== 'İptal') {
    const today = new Date().toISOString().split('T')[0];
    const dueDate = data.due_date !== undefined ? data.due_date : (existing as any).due_date;
    if (remainingAmount <= 0) autoStatus = 'Ödendi';
    else if (dueDate && dueDate < today) autoStatus = 'Gecikti';
    else if (remainingAmount > 0 && dueDate && dueDate >= today) autoStatus = 'Bekliyor';
  }

  return update('promissory_notes', id, {
    ...data,
    remaining_amount: remainingAmount,
    status: autoStatus,
  });
}

export async function deletePromissoryNote(id: string): Promise<DbResult<boolean>> {
  return remove('promissory_notes', id);
}

/**
 * Müşterinin açık senetlerini getirir (Bekliyor, Kısmi Ödendi, Gecikti).
 * Ödenmiş, iptal edilmiş senetleri dahil etmez.
 * Kullanım: Yeni satışta "Mevcut Senede Ekle" seçeneği için.
 */
export async function getCustomerOpenNotes(customerId: string): Promise<DbResult<any[]>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { data, error } = await supabase
      .from('promissory_notes')
      .select('*')
      .eq('business_id', bizId)
      .eq('customer_id', customerId)
      .not('status', 'in', '("Ödendi","İptal")')
      .gt('remaining_amount', 0)
      .order('updated_at', { ascending: false, nullsFirst: false })
      .order('created_at', { ascending: false, nullsFirst: false });

    if (error) return { data: null, error: handleError(error) };

    // Enrich with linked sales count from movements
    const notes = (data || []) as any[];
    if (notes.length > 0) {
      const noteIds = notes.map(n => n.id);
      const { data: movements } = await supabase
        .from('promissory_note_movements')
        .select('promissory_note_id, sale_id')
        .in('promissory_note_id', noteIds)
        .eq('movement_type', 'sale_added');

      const movementCounts: Record<string, number> = {};
      (movements || []).forEach((m: any) => {
        movementCounts[m.promissory_note_id] = (movementCounts[m.promissory_note_id] || 0) + 1;
      });

      notes.forEach(n => {
        n.linked_sales_count = movementCounts[n.id] || 0;
        n.current_remaining = n.remaining_amount !== undefined
          ? n.remaining_amount
          : ((n.amount || 0) - (n.paid_amount || 0));
      });
    }

    return { data: notes, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

/**
 * Senet hareketlerini getirir.
 */
export async function getPromissoryNoteMovements(noteId: string): Promise<DbResult<any[]>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { data, error } = await supabase
      .from('promissory_note_movements')
      .select('*')
      .eq('business_id', bizId)
      .eq('promissory_note_id', noteId)
      .order('created_at', { ascending: false });

    if (error) return { data: null, error: handleError(error) };
    return { data: data as any[], error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

/**
 * Atomik RPC: Yeni satış oluştur ve mevcut senede bağla.
 * Tüm işlemler tek transaction içinde yapılır, bir adım başarısız olursa
 * tüm işlem geri alınır.
 */
export interface CreateSaleAttachToNotePayload {
  customer_id: string;
  sale: Record<string, any>;
  sale_items: Record<string, any>[];
  delivery?: Record<string, any> | null;
  note_id?: string | null;
  down_payment_date?: string | null;
  installments?: Record<string, any>[];
}

export async function createSaleAndAttachToNote(
  payload: CreateSaleAttachToNotePayload
): Promise<DbResult<any>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { data, error } = await supabase.rpc('create_sale_and_attach_to_existing_note', {
      p_business_id: bizId,
      p_customer_id: payload.customer_id,
      p_sale: payload.sale,
      p_sale_items: payload.sale_items,
      p_delivery: payload.delivery || null,
      p_note_id: payload.note_id || null,
      p_down_payment_date: payload.down_payment_date || null,
      p_installments: payload.installments && payload.installments.length > 0 ? payload.installments : null,
    });

    if (error) return { data: null, error: handleError(error) };

    const result = data as any;
    if (result && result.success === false) {
      return { data: null, error: result.error || 'İşlem başarısız' };
    }

    return { data: result, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

/**
 * Atomik RPC: Yeni satış oluştur (nakit / peşinatlı / taksitli / yeni senetli).
 * Mevcut bir senede eklemek için createSaleAndAttachToNote kullanılır.
 * Tüm işlemler tek transaction içinde yapılır, bir adım başarısız olursa
 * tüm işlem geri alınır. Belge numarası (SLS/SNT/MAK) yalnızca RPC içinde,
 * gerçek kayıt anında üretilir.
 */
export interface CreateSaleTransactionPayload {
  customer_id: string;
  sale: Record<string, any>;
  sale_items: Record<string, any>[];
  delivery?: Record<string, any> | null;
  installments?: Record<string, any>[];
  notes?: Record<string, any>[];
}

export async function createSaleTransaction(
  payload: CreateSaleTransactionPayload
): Promise<DbResult<any>> {
  try {
    const { data, error } = await supabase.rpc('create_sale_transaction', { payload });

    if (error) return { data: null, error: handleError(error) };

    const result = data as any;
    if (result && result.success === false) {
      return { data: null, error: result.error || 'İşlem başarısız' };
    }

    return { data: result, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

/**
 * Atomik RPC: Satışı ve tüm bağlı kayıtlarını (kalemler, ödemeler, taksitler,
 * teslimat, stok hareketleri, senet/tahsis) geri alarak siler; müşteri
 * toplamlarını çifte sayım yapmadan yeniden hesaplar. İcra dosyası bağlıysa
 * reddeder (hukuki kayıt korunur).
 */
export async function deleteSaleTransaction(
  saleId: string,
  reason?: string
): Promise<DbResult<any>> {
  try {
    const { data, error } = await supabase.rpc('delete_sale_transaction', {
      p_sale_id: saleId,
      p_reason: reason || null,
    });

    if (error) return { data: null, error: handleError(error) };

    const result = data as any;
    if (result && result.success === false) {
      return { data: null, error: result.error || 'Silme işlemi başarısız' };
    }

    return { data: result, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

/**
 * Kitap filtre seçeneklerini RPC üzerinden getirir.
 * normalize_book_no ile gruplar, sayısal sıralama yapar, kayıt sayısını döndürür.
 */
export interface BookFilterOption {
  book_value: string;
  display_label: string;
  note_count: number;
}

export async function getPromissoryNoteBookFilters(): Promise<DbResult<BookFilterOption[]>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { data, error } = await supabase.rpc('get_promissory_note_book_filters', {
      p_business_id: bizId,
    });

    if (error) return { data: null, error: handleError(error) };
    return { data: (data as BookFilterOption[]) || [], error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

// ============================================================
// BUSINESS
// ============================================================

export async function getCurrentBusiness(): Promise<DbResult<any>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { data, error } = await supabase
      .from('businesses')
      .select('*')
      .eq('id', bizId)
      .maybeSingle();

    if (error) return { data: null, error: handleError(error) };
    return { data, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

export async function createBusiness(data: any): Promise<DbResult<any>> {
  try {
    const { data: created, error } = await supabase
      .from('businesses')
      .insert(sanitizeItem(data))
      .select()
      .single();

    if (error) return { data: null, error: handleError(error) };
    return { data: created, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

export async function addBusinessMember(businessId: string, userId: string, role: string = 'admin'): Promise<DbResult<any>> {
  try {
    const { data, error } = await supabase
      .from('business_members')
      .insert({ business_id: businessId, user_id: userId, role, status: 'Aktif' })
      .select()
      .single();

    if (error) return { data: null, error: handleError(error) };
    return { data, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

// ============================================================
// PROFILE
// ============================================================
export async function getCurrentProfile(): Promise<DbResult<any>> {
  try {
    if (!supabase || !supabase.auth) return { data: null, error: 'Sunucu bağlantısı kurulamadı' };

    const result = await supabase.auth.getSession();
    const session = result?.data?.session;
    if (!session?.user?.id) return { data: null, error: 'Oturum bulunamadı' };

    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('user_id', session.user.id)
      .maybeSingle();

    if (error) return { data: null, error: handleError(error) };
    return { data, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

export async function createProfile(data: any): Promise<DbResult<any>> {
  return create('profiles', data);
}

// ============================================================
// ENFORCEMENT CASES (İcra Takibi)
// ============================================================
export async function getEnforcementCases(): Promise<DbResult<any[]>> {
  return getAll('enforcement_cases');
}

export async function getEnforcementCaseById(id: string): Promise<DbResult<any>> {
  return getById('enforcement_cases', id);
}

export async function getEnforcementCasesByCustomerId(customerId: string): Promise<DbResult<any[]>> {
  return queryEq('enforcement_cases', 'customer_id', customerId);
}

export async function getActiveEnforcementCaseByCustomerId(customerId: string): Promise<DbResult<any>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { data, error } = await supabase
      .from('enforcement_cases')
      .select('*')
      .eq('business_id', bizId)
      .eq('customer_id', customerId)
      .not('status', 'in', '("Tahsil Edildi","Dosya Kapatıldı","İptal Edildi")')
      .order('created_at', { ascending: false })
      .limit(1);

    if (error) return { data: null, error: handleError(error) };
    return { data: (data && data.length > 0 ? data[0] : null) as any, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

export async function createEnforcementCase(data: any): Promise<DbResult<any>> {
  try {
    // Check for duplicate active case
    const { data: existing } = await getActiveEnforcementCaseByCustomerId(data.customer_id);
    if (existing) return { data: null, error: 'Bu müşteri için zaten aktif bir icra dosyası bulunuyor.' };

    const principalAmount = data.total_debt || 0;
    const paidAmount = data.paid_after_enforcement || 0;

    // note_ids is NOT a column in enforcement_cases — extract for note linking only
    const { note_ids, ...insertData } = data;

    const MAX_RETRIES = 3;
    let created: any = null;
    let lastError: any = null;

    for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
      // Generate case_no if not provided (re-generate on each retry)
      if (!insertData.case_no || attempt > 1) {
        const { data: caseNo } = await generateEnforcementCaseNo();
        if (caseNo) insertData.case_no = caseNo;
      }

      const bizId = await getUserBusinessId();
      const { data: result, error } = await supabase
        .from('enforcement_cases')
        .insert({
          ...sanitizeItem(insertData),
          remaining_debt: Math.max(0, principalAmount - paidAmount),
          status: data.status || 'Hazırlık Aşamasında',
          business_id: bizId,
        })
        .select()
        .single();

      if (!error) {
        created = result;
        break;
      }

      // If it's a unique constraint violation on case_no, retry with a new number
      if ((error as any).code === '23505' && (error as any).message?.includes('uq_cases_business_case_no')) {
        lastError = error;
        console.warn(`[createEnforcementCase] case_no çakışması, retry ${attempt}/${MAX_RETRIES}: ${insertData.case_no}`);
        continue;
      }

      // Any other error: fail immediately
      return { data: null, error: handleError(error) };
    }

    if (!created) {
      return { data: null, error: `İcra dosya numarası oluşturulamadı (${MAX_RETRIES} deneme). Son hata: ${handleError(lastError)}` };
    }

    // Update customer status to 'İcrada'
    await updateCustomer(data.customer_id, { status: 'İcrada' });

    // Mark associated notes
    if (note_ids && Array.isArray(note_ids)) {
      for (const noteId of note_ids) {
        await supabase
          .from('promissory_notes')
          .update({ enforcement_status: true, enforcement_case_id: (created as any).id })
          .eq('id', noteId);
      }
    }

    return { data: created, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

export async function updateEnforcementCase(id: string, data: any): Promise<DbResult<any>> {
  try {
    const updates: any = { ...data };

    if (data.status === 'Tahsil Edildi' || data.status === 'Dosya Kapatıldı' || data.status === 'İptal Edildi') {
      updates.closed_at = updates.closed_at || new Date().toISOString();
    }

    if (data.paid_after_enforcement !== undefined) {
      const { data: existing } = await getEnforcementCaseById(id);
      if (existing) {
        const totalDebt = parseFloat((existing as any).total_debt || 0);
        const paid = parseFloat(data.paid_after_enforcement || 0);
        updates.remaining_debt = Math.max(0, totalDebt - paid);
        if (updates.remaining_debt <= 0 && !data.status) {
          updates.status = 'Tahsil Edildi';
          updates.closed_at = new Date().toISOString();
        }
      }
    }

    return update('enforcement_cases', id, updates);
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

export async function generateEnforcementCaseNo(): Promise<DbResult<string>> {
  return getNextDocumentNumber('ICR');
}

// ============================================================
// PAYMENT PROMISES (Ödeme Sözleri)
// ============================================================
export async function getPaymentPromises(): Promise<DbResult<any[]>> {
  return getAll('payment_promises');
}

export async function getPaymentPromiseById(id: string): Promise<DbResult<any>> {
  return getById('payment_promises', id);
}

export async function getPaymentPromisesByCustomerId(customerId: string): Promise<DbResult<any[]>> {
  return queryEq('payment_promises', 'customer_id', customerId);
}

export async function getPaymentPromisesBySaleId(saleId: string): Promise<DbResult<any[]>> {
  return queryEq('payment_promises', 'sale_id', saleId);
}

export async function getPaymentPromisesByStatus(status: string): Promise<DbResult<any[]>> {
  return queryEq('payment_promises', 'status', status);
}

export async function getTodayPaymentPromises(): Promise<DbResult<any[]>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const today = new Date().toISOString().split('T')[0];
    const { data, error } = await supabase
      .from('payment_promises')
      .select('*')
      .eq('business_id', bizId)
      .eq('promised_date', today)
      .not('status', 'in', '("İptal","Ödeme Alındı")')
      .not('follow_up_status', 'in', '("Ödeme Yaptı","İptal Edildi")')
      .order('created_at', { ascending: false });

    if (error) return { data: null, error: handleError(error) };
    return { data: (data as any[]) || [], error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

export async function getPaymentPromisesByDateRange(gte: string, lte: string): Promise<DbResult<any[]>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { data, error } = await supabase
      .from('payment_promises')
      .select('*')
      .eq('business_id', bizId)
      .gte('promised_date', gte)
      .lte('promised_date', lte)
      .not('status', 'in', '("İptal","Ödeme Alındı")')
      .order('promised_date', { ascending: true });

    if (error) return { data: null, error: handleError(error) };
    return { data: (data as any[]) || [], error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

export async function getOverduePaymentPromises(): Promise<DbResult<any[]>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const today = new Date().toISOString().split('T')[0];
    const { data, error } = await supabase
      .from('payment_promises')
      .select('*')
      .eq('business_id', bizId)
      .lt('promised_date', today)
      .not('status', 'in', '("İptal","Ödeme Alındı")')
      .not('follow_up_status', 'in', '("Ödeme Yaptı","İptal Edildi")')
      .order('promised_date', { ascending: true });

    if (error) return { data: null, error: handleError(error) };
    return { data: (data as any[]) || [], error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

export async function createPaymentPromise(data: any): Promise<DbResult<any>> {
  const promisedAmount = Number(data.promised_amount) || 0;
  // Fetch live debt snapshot at creation time
  let debtSnapshot = null;
  try {
    const { data: debtData } = await getCustomerDebtComponents(data.customer_id);
    if (debtData) debtSnapshot = (debtData as any).total_debt || 0;
  } catch { /* silent */ }

  return create('payment_promises', {
    ...data,
    paid_amount: data.paid_amount || 0,
    remaining_amount: promisedAmount,
    status: data.status || 'Bekliyor',
    follow_up_status: data.follow_up_status || data.status || 'Bekliyor',
    debt_snapshot_at_creation: debtSnapshot,
    source_type: data.source_type || (data.sale_id ? 'sale' : data.promissory_note_id ? 'promissory_note' : null),
  });
}

export async function updatePaymentPromise(id: string, data: any): Promise<DbResult<any>> {
  return update('payment_promises', id, data);
}

export async function deletePaymentPromise(id: string): Promise<DbResult<boolean>> {
  return remove('payment_promises', id);
}

// ============================================================
// PAYMENT PROMISE ACTIVITIES (Görüşme Geçmişi)
// ============================================================
export async function getPaymentPromiseActivities(promiseId: string): Promise<DbResult<any[]>> {
  return queryEq('payment_promise_activities', 'payment_promise_id', promiseId);
}

export async function getPaymentPromiseActivitiesByCustomerId(customerId: string): Promise<DbResult<any[]>> {
  return queryEq('payment_promise_activities', 'customer_id', customerId);
}

export async function createPaymentPromiseActivity(data: any): Promise<DbResult<any>> {
  return create('payment_promise_activities', data);
}

// Dashboard için ödeme sözü takip özetleri
export interface PromiseFollowUpCounts {
  today_call: number;
  recall: number;
  evening: number;
  defaulted: number;
}

export async function getPromiseFollowUpCounts(): Promise<DbResult<PromiseFollowUpCounts>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const today = new Date().toLocaleDateString('en-CA', { timeZone: 'Europe/Istanbul' });

    const { data, error } = await supabase
      .from('payment_promises')
      .select('follow_up_status, next_follow_up_date')
      .eq('business_id', bizId)
      .not('follow_up_status', 'in', '("Ödeme Yaptı","İptal Edildi")');

    if (error) return { data: null, error: handleError(error) };

    const rows = (data || []) as any[];
    const counts: PromiseFollowUpCounts = {
      today_call: 0,
      recall: 0,
      evening: 0,
      defaulted: 0,
    };

    rows.forEach((r: any) => {
      const status = r.follow_up_status || 'Bekliyor';
      if (status === 'Aranacak' && r.next_follow_up_date === today) counts.today_call++;
      if (status === 'Tekrar Aranacak') counts.recall++;
      if (status === 'Akşam Ödeyecek') counts.evening++;
      if (status === 'Sözünü Tutmadı') counts.defaulted++;
    });

    return { data: counts, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

export async function getPaymentPromisesByFollowUpStatus(status: string): Promise<DbResult<any[]>> {
  return queryEq('payment_promises', 'follow_up_status', status);
}

export async function getPaymentPromisesByFollowUpStatuses(statuses: string[]): Promise<DbResult<any[]>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { data, error } = await supabase
      .from('payment_promises')
      .select('*')
      .eq('business_id', bizId)
      .in('follow_up_status', statuses)
      .order('next_follow_up_date', { ascending: true, nullsFirst: false })
      .order('promised_date', { ascending: true });

    if (error) return { data: null, error: handleError(error) };
    return { data: (data as any[]) || [], error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

/**
 * Takip durumunu günceller ve aktivite kaydı oluşturur (atomic işlem değil, best-effort)
 */
export async function updatePaymentPromiseFollowUpStatus(
  id: string,
  payload: {
    follow_up_status: string;
    next_follow_up_date?: string | null;
    next_follow_up_time?: string | null;
    last_contact_note?: string;
    contacted_by?: string;
    last_contacted_at?: string;
  }
): Promise<DbResult<any>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    // Önce mevcut kaydı al
    const { data: existing, error: fetchErr } = await getPaymentPromiseById(id);
    if (fetchErr || !existing) return { data: null, error: fetchErr || 'Ödeme sözü bulunamadı' };

    const oldStatus = (existing as any).follow_up_status || 'Bekliyor';
    const oldFollowUpDate = (existing as any).next_follow_up_date || null;
    const oldFollowUpTime = (existing as any).next_follow_up_time || null;

    const updates: any = {
      follow_up_status: payload.follow_up_status,
      updated_at: new Date().toISOString(),
    };

    if (payload.next_follow_up_date !== undefined) updates.next_follow_up_date = payload.next_follow_up_date || null;
    if (payload.next_follow_up_time !== undefined) updates.next_follow_up_time = payload.next_follow_up_time || null;
    if (payload.last_contact_note !== undefined) updates.last_contact_note = payload.last_contact_note;
    if (payload.contacted_by !== undefined) updates.contacted_by = payload.contacted_by;
    if (payload.last_contacted_at !== undefined) updates.last_contacted_at = payload.last_contacted_at;

    // Eğer original_promise_date henüz set edilmemişse, şimdi set et
    if (!(existing as any).original_promise_date && (existing as any).promised_date) {
      updates.original_promise_date = (existing as any).promised_date;
    }

    const { data: updated, error: updErr } = await update('payment_promises', id, updates);
    if (updErr) return { data: null, error: updErr };

    // Aktivite kaydı oluştur
    const activityNote = payload.last_contact_note || `Durum değişti: ${oldStatus} → ${payload.follow_up_status}`;
    await createPaymentPromiseActivity({
      payment_promise_id: id,
      customer_id: (existing as any).customer_id,
      activity_type: payload.follow_up_status,
      activity_date: new Date().toISOString().split('T')[0],
      note: activityNote,
      old_status: oldStatus,
      new_status: payload.follow_up_status,
      old_follow_up_date: oldFollowUpDate,
      new_follow_up_date: payload.next_follow_up_date || null,
      old_follow_up_time: oldFollowUpTime,
      new_follow_up_time: payload.next_follow_up_time || null,
      created_by: payload.contacted_by || 'Admin',
    });

    return { data: updated, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

export async function getPaymentPromiseActivitiesFull(promiseId: string): Promise<DbResult<any[]>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { data, error } = await supabase
      .from('payment_promise_activities')
      .select('*')
      .eq('business_id', bizId)
      .eq('payment_promise_id', promiseId)
      .order('created_at', { ascending: false });

    if (error) return { data: null, error: handleError(error) };
    return { data: (data as any[]) || [], error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

// ============================================================
// CUSTOMER CONTACTS (İletişim Geçmişi)
// ============================================================
export async function getCustomerContacts(customerId: string): Promise<DbResult<any[]>> {
  return queryEq('customer_contacts', 'customer_id', customerId);
}

export async function createCustomerContact(data: any): Promise<DbResult<any>> {
  return create('customer_contacts', data);
}

// ============================================================
// GENERIC CRUD (localStorageDb uyumluluğu için)
// ============================================================
export async function genericCreate(table: string, item: any): Promise<DbResult<any>> {
  return create(table, item);
}

export async function genericUpdate(table: string, id: string, updates: any): Promise<DbResult<any>> {
  return update(table, id, updates);
}

export async function genericRemove(table: string, id: string): Promise<DbResult<boolean>> {
  return remove(table, id);
}

export async function genericQueryEq(table: string, column: string, value: any): Promise<DbResult<any[]>> {
  return queryEq(table, column, value);
}

export async function genericGetAll(table: string): Promise<DbResult<any[]>> {
  return getAll(table);
}

export async function genericGetById(table: string, id: string): Promise<DbResult<any>> {
  return getById(table, id);
}

// ============================================================
// COMPLEX OPERATIONS (localStorageDb'den taşınan)
// ============================================================

/**
 * Transaction-safe numara üretimi — document_sequences tablosu üzerinden
 * atomik INSERT ON CONFLICT DO UPDATE ile çalışır.
 * Eş zamanlı iki istek aynı numarayı alamaz.
 */
export async function getNextDocumentNumber(documentType: 'CUST' | 'SLS' | 'MAK' | 'SNT' | 'ICR' | 'TKL' | 'LGC' | 'LOAN'): Promise<DbResult<string>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { data, error } = await supabase.rpc('get_next_document_number', {
      p_business_id: bizId,
      p_document_type: documentType,
    });

    if (error) return { data: null, error: handleError(error) };
    return { data: data as string, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

export async function generateNoteNo(): Promise<DbResult<string>> {
  return getNextDocumentNumber('SNT');
}

export async function generateCustomerNo(): Promise<DbResult<string>> {
  return getNextDocumentNumber('CUST');
}

export async function generateReceiptNo(): Promise<DbResult<string>> {
  return getNextDocumentNumber('MAK');
}

export async function createLegacyCustomerWithNote(payload: {
  customer: any;
  note: any;
  legacyPayment?: { amount: number; payment_date: string } | null;
}): Promise<DbResult<{ customer: any; note: any; payment: any | null }>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    // ── Atomik RPC: tüm devir işlemi tek transaction'da ──
    // Müşteri no → document_sequences üzerinden atomik üretilir
    // Senet no → document_sequences üzerinden atomik üretilir
    // Ödeme makbuz no → document_sequences üzerinden atomik üretilir
    // Race condition yok, retry loop yok, ara hata toast'u yok
    const rpcPayload: any = {
      business_id: bizId,
      customer: {
        full_name: payload.customer.full_name || '',
        phone: payload.customer.phone || '',
        customer_no: payload.customer.customer_no || null,
        second_phone: payload.customer.second_phone || null,
        address: payload.customer.address || null,
        city: payload.customer.city || null,
        district: payload.customer.district || null,
        identity_number: payload.customer.identity_number || null,
        notes: payload.customer.notes || null,
        status: payload.customer.status || 'Aktif',
      },
      note: payload.note ? {
        legacy_note_no: payload.note.note_no || payload.note.legacy_note_no || null,
        purchase_date: payload.note.purchase_date || null,
        amount: payload.note.amount || 0,
        paid_amount: payload.note.paid_amount || 0,
        issue_date: payload.note.issue_date || null,
        due_date: payload.note.due_date || null,
        note_book_no: payload.note.note_book_no || '',
        note_page_no: payload.note.note_page_no || '',
        note_sequence_no: payload.note.note_sequence_no || null,
        guarantor_name: payload.note.guarantor_name || null,
        guarantor_phone: payload.note.guarantor_phone || null,
        description: payload.note.description || null,
        status: payload.note.status || null,
      } : null,
      legacy_payment: payload.legacyPayment && payload.legacyPayment.amount > 0 ? {
        amount: payload.legacyPayment.amount,
        payment_date: payload.legacyPayment.payment_date || null,
      } : null,
    };

    const { data, error } = await supabase.rpc('create_legacy_transfer_transaction', {
      payload: rpcPayload,
    });

    if (error) return { data: null, error: handleError(error) };

    const result = data as any;
    if (!result || result.success === false) {
      return {
        data: null,
        error: (result && result.error) || 'Devir kaydı oluşturulamadı.',
        errorCode: (result && result.code) || 'UNKNOWN',
      };
    }

    return {
      data: {
        customer: result.customer || null,
        note: result.note || null,
        payment: result.payment || null,
      },
      error: null,
    };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

export async function addLegacyNoteToCustomer(customerId: string, noteData: any): Promise<DbResult<{ note: any; payment: any | null }>> {
  try {
    const { data: customer, error: custErr } = await getCustomerById(customerId);
    if (custErr || !customer) return { data: null, error: custErr || 'Müşteri bulunamadı.' };

    const { data: noteNo } = await generateNoteNo();
    const finalNoteNo = noteData.note_no || noteNo || `SNT-${new Date().getFullYear()}-001`;

    const principalAmount = noteData.amount || 0;
    const paidAmount = noteData.paid_amount || 0;
    const remainingAmount = principalAmount - paidAmount;

    const today = new Date().toISOString().split('T')[0];
    let autoStatus = 'Bekliyor';
    if (remainingAmount <= 0) autoStatus = 'Ödendi';
    else if (noteData.due_date && noteData.due_date < today) autoStatus = 'Gecikti';

    const { data: createdNote, error: noteErr } = await create('promissory_notes', {
      ...noteData,
      note_no: finalNoteNo,
      customer_id: customerId,
      customer_name: (customer as any).full_name,
      customer_phone: (customer as any).phone,
      sale_id: null,
      sale_no: null,
      source_type: 'legacy_opening_balance',
      paid_amount: paidAmount,
      remaining_amount: remainingAmount,
      status: noteData.status || autoStatus,
    });

    if (noteErr) return { data: null, error: noteErr };

    let createdPayment: any = null;
    if (paidAmount > 0) {
      const { data: legacyReceipt } = await getNextDocumentNumber('LGC');
      const receiptNo = legacyReceipt || `LGCY-${new Date().getFullYear()}-0001`;
      const { data: payment } = await create('payments', {
        customer_id: customerId,
        customer_name: (customer as any).full_name,
        sale_id: null,
        sale_no: null,
        promissory_note_id: (createdNote as any).id,
        payment_date: noteData.purchase_date || today,
        amount: paidAmount,
        payment_method: 'Devir Kaydı',
        receipt_no: receiptNo,
        description: 'Eski sistemde daha önce yapılmış toplam ödeme',
        received_by: 'Sistem',
        is_legacy_payment: true,
      });
      createdPayment = payment;
    }

    // Update customer totals
    await updateCustomer(customerId, {
      total_sales_amount: (customer as any).total_sales_amount || 0,
      total_paid_amount: ((customer as any).total_paid_amount || 0) + paidAmount,
      total_debt_amount: ((customer as any).total_debt_amount || 0) + remainingAmount,
      last_purchase_date: noteData.purchase_date || (customer as any).last_purchase_date,
      status: (((customer as any).total_debt_amount || 0) + remainingAmount) > 0 ? 'Borçlu' : 'Borcu Yok',
    });

    return { data: { note: createdNote, payment: createdPayment }, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

export async function createNotePayment(payload: {
  noteId: string;
  amount: number;
  paymentDate: string;
  paymentMethod: string;
  receiptNo: string;
  description: string;
}): Promise<DbResult<{ note: any; payment: any }>> {
  try {
    const { data: note, error: noteErr } = await getPromissoryNoteById(payload.noteId);
    if (noteErr || !note) return { data: null, error: noteErr || 'Senet bulunamadı.' };

    const remainingAmount = (note as any).remaining_amount !== undefined
      ? (note as any).remaining_amount
      : (((note as any).amount || 0) - ((note as any).paid_amount || 0));

    if (payload.amount > remainingAmount) {
      return { data: null, error: `Ödeme tutarı kalan senet borcundan (${remainingAmount.toLocaleString('tr-TR')} ₺) fazla olamaz.` };
    }

    const newPaid = ((note as any).paid_amount || 0) + payload.amount;
    const newRemaining = ((note as any).amount || 0) - newPaid;

    // Update note
    await updatePromissoryNote(payload.noteId, {
      paid_amount: newPaid,
      remaining_amount: newRemaining,
    });

    // Create payment
    const { data: createdPayment } = await create('payments', {
      customer_id: (note as any).customer_id,
      customer_name: (note as any).customer_name,
      sale_id: (note as any).sale_id,
      sale_no: (note as any).sale_no,
      promissory_note_id: payload.noteId,
      source_type: 'promissory_note',
      payment_date: payload.paymentDate,
      amount: payload.amount,
      payment_method: payload.paymentMethod,
      receipt_no: payload.receiptNo,
      description: payload.description || 'Senet ödemesi',
      received_by: 'Admin',
      is_legacy_payment: false,
    });

    // Create cash movement
    await create('cash_movements', {
      type: 'Gelir',
      description: `${(note as any).customer_name || ''} - Senet ödemesi (${(note as any).note_no})`,
      amount: payload.amount,
      date: payload.paymentDate,
      category: 'Tahsilat',
      payment_method: payload.paymentMethod,
      reference: (note as any).note_no,
      recorded_by: 'Admin',
      notes: payload.description || '',
      movement_date: payload.paymentDate,
    });

    // Update customer totals
    const { data: customer } = await getCustomerById((note as any).customer_id);
    if (customer) {
      await updateCustomer((note as any).customer_id, {
        total_paid_amount: ((customer as any).total_paid_amount || 0) + payload.amount,
        total_debt_amount: Math.max(0, ((customer as any).total_debt_amount || 0) - payload.amount),
        status: (((customer as any).total_debt_amount || 0) - payload.amount) <= 0 ? 'Borcu Yok' : 'Borçlu',
      });
    }

    // Get updated note
    const { data: updatedNote } = await getPromissoryNoteById(payload.noteId);

    return { data: { note: updatedNote, payment: createdPayment }, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

// ============================================================
// LEGACY DEBT PAYMENT (senetsiz eski sistem borcu ödemesi)
// ============================================================

export async function createLegacyDebtPayment(payload: {
  customerId: string;
  amount: number;
  paymentDate: string;
  paymentMethod: string;
  receiptNo: string;
  description: string;
  receivedBy: string;
}): Promise<DbResult<{ payment: any; customer: any; updatedNotes: any[] }>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { data: customer, error: custErr } = await getCustomerById(payload.customerId);
    if (custErr || !customer) return { data: null, error: custErr || 'Müşteri bulunamadı.' };

    const currentDebt = (customer as any).total_debt_amount || 0;
    if (payload.amount > currentDebt) {
      return { data: null, error: 'Ödeme tutarı kalan borçtan fazla olamaz.' };
    }

    // 1. Find open legacy promissory notes (source_type = legacy_opening_balance, not paid/cancelled)
    const { data: legacyNotes } = await supabase
      .from('promissory_notes')
      .select('*')
      .eq('business_id', bizId)
      .eq('customer_id', payload.customerId)
      .eq('source_type', 'legacy_opening_balance')
      .not('status', 'in', '("Ödendi","İptal")')
      .order('due_date', { ascending: true, nullsFirst: false });

    const openLegacyNotes = (legacyNotes || []) as any[];
    const updatedNotes: any[] = [];

    // 2. Deduct from legacy notes (FIFO - oldest first)
    let remainingToDeduct = payload.amount;
    let primaryNoteId: string | null = null;

    for (const note of openLegacyNotes) {
      if (remainingToDeduct <= 0) break;

      const noteRemaining = note.remaining_amount !== undefined
        ? parseFloat(note.remaining_amount)
        : (parseFloat(note.amount || 0) - parseFloat(note.paid_amount || 0));

      if (noteRemaining <= 0) continue;

      if (!primaryNoteId) primaryNoteId = note.id;

      const deductFromNote = Math.min(remainingToDeduct, noteRemaining);
      const newPaid = parseFloat(note.paid_amount || 0) + deductFromNote;
      const newRemaining = parseFloat(note.amount || 0) - newPaid;

      // Determine status
      let newStatus = note.status;
      if (newRemaining <= 0) {
        newStatus = 'Ödendi';
      } else if (deductFromNote > 0 && newRemaining > 0) {
        newStatus = 'Kısmi Ödendi';
      }

      const { data: updatedNote, error: updErr } = await supabase
        .from('promissory_notes')
        .update({
          paid_amount: newPaid,
          remaining_amount: newRemaining,
          status: newStatus,
          updated_at: new Date().toISOString(),
        })
        .eq('id', note.id)
        .eq('business_id', bizId)
        .select()
        .single();

      if (!updErr && updatedNote) {
        updatedNotes.push(updatedNote);
      }

      remainingToDeduct -= deductFromNote;
    }

    // 3. Create payment record
    const { data: createdPayment, error: payErr } = await create('payments', {
      customer_id: payload.customerId,
      customer_name: (customer as any).full_name,
      sale_id: null,
      sale_no: null,
      promissory_note_id: primaryNoteId,
      source_type: 'legacy_debt',
      payment_date: payload.paymentDate,
      amount: payload.amount,
      payment_method: payload.paymentMethod,
      receipt_no: payload.receiptNo,
      description: payload.description || 'Eski sistem borcu ödemesi',
      received_by: payload.receivedBy,
      is_legacy_payment: false,
    });

    if (payErr) return { data: null, error: payErr };

    // 4. Create cash movement
    const noteRef = primaryNoteId && updatedNotes.length > 0
      ? (updatedNotes[0] as any).note_no || 'Eski Sistem Borcu'
      : 'Eski Sistem Borcu';

    await create('cash_movements', {
      type: 'Gelir',
      description: `${(customer as any).full_name || ''} - Eski sistem borcu ödemesi${primaryNoteId ? ' (' + noteRef + ')' : ''}`,
      amount: payload.amount,
      date: payload.paymentDate,
      category: 'Tahsilat',
      payment_method: payload.paymentMethod,
      reference: noteRef,
      recorded_by: payload.receivedBy,
      notes: payload.description || '',
      movement_date: payload.paymentDate,
    });

    // 5. Update customer totals
    const newTotalPaid = ((customer as any).total_paid_amount || 0) + payload.amount;
    const newTotalDebt = Math.max(0, currentDebt - payload.amount);

    await updateCustomer(payload.customerId, {
      total_paid_amount: newTotalPaid,
      total_debt_amount: newTotalDebt,
      status: newTotalDebt <= 0 ? 'Borcu Yok' : 'Borçlu',
    });

    const { data: updatedCustomer } = await getCustomerById(payload.customerId);

    return { data: { payment: createdPayment, customer: updatedCustomer, updatedNotes }, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

// ============================================================
// CUSTOMER TOTALS RECALCULATION (Import sonrası senkronizasyon için)
// ============================================================

export async function recalculateAllCustomerTotals(): Promise<DbResult<{ updated: number; errors: string[] }>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    // Tüm müşterileri çek
    const { data: customers, error: custErr } = await supabase
      .from('customers')
      .select('id')
      .eq('business_id', bizId);

    if (custErr) return { data: null, error: handleError(custErr) };
    if (!customers || customers.length === 0) return { data: { updated: 0, errors: [] }, error: null };

    // Tüm satışları, ödemeleri, senetleri ve icra dosyalarını çek (tek seferde)
    const [salesRes, notesRes, paymentsRes, enforcementRes] = await Promise.all([
      supabase.from('sales').select('customer_id,total_amount,remaining_amount,payment_status').eq('business_id', bizId),
      supabase.from('promissory_notes').select('customer_id,amount,paid_amount,remaining_amount,status,source_type,sale_id').eq('business_id', bizId),
      supabase.from('payments').select('customer_id,amount').eq('business_id', bizId),
      supabase.from('enforcement_cases').select('customer_id,status').eq('business_id', bizId),
    ]);

    const sales = (salesRes.data || []) as any[];
    const notes = (notesRes.data || []) as any[];
    const payments = (paymentsRes.data || []) as any[];
    const enforcement = (enforcementRes.data || []) as any[];

    const errors: string[] = [];
    let updated = 0;

    for (const cust of customers) {
      const cid = cust.id;

      const custSales = sales.filter((s: any) => s.customer_id === cid);
      const custNotes = notes.filter((n: any) => n.customer_id === cid && n.status !== 'İptal');
      const custPayments = payments.filter((p: any) => p.customer_id === cid);
      const custEnf = enforcement.filter((e: any) => e.customer_id === cid);

      // Satışlardan gelen toplamlar
      const totalSalesAmount = custSales.reduce((sum: number, s: any) => sum + (s.total_amount || 0), 0);
      const remainingFromSales = custSales.reduce((sum: number, s: any) => sum + (s.remaining_amount || 0), 0);

      // Senetlerden gelen toplamlar (sadece satışa bağlı olmayan legacy senetler)
      const legacyNotes = custNotes.filter((n: any) => n.source_type === 'legacy_opening_balance' || !n.sale_id);
      const legacyPrincipal = legacyNotes.reduce((sum: number, n: any) => sum + (n.amount || 0), 0);
      const legacyRemaining = legacyNotes.reduce((sum: number, n: any) => sum + (n.remaining_amount !== undefined ? n.remaining_amount : ((n.amount || 0) - (n.paid_amount || 0))), 0);
      const legacyPaid = legacyPrincipal - legacyRemaining;

      // Toplam ödemeler (senet ödemeleri dahil)
      const totalPaidFromPayments = custPayments.reduce((sum: number, p: any) => sum + (p.amount || 0), 0);

      // Nihai hesaplama — legacy senedi satış değildir, toplam alışverişe dahil edilmez
      const finalTotalSales = totalSalesAmount;
      const finalTotalPaid = totalPaidFromPayments;
      const finalTotalDebt = remainingFromSales + legacyRemaining;

      // Eğer hiç satış, legacy senet veya ödeme yoksa müşterinin mevcut değerlerini koru
      const hasAnyActivity = totalSalesAmount > 0 || legacyPrincipal > 0 || totalPaidFromPayments > 0;

      const { data: currentCust, error: currErr } = await supabase
        .from('customers')
        .select('total_sales_amount, total_paid_amount, total_debt_amount')
        .eq('id', cid)
        .maybeSingle();

      const safeTotalSales = hasAnyActivity ? finalTotalSales : (currentCust ? (currentCust as any).total_sales_amount || 0 : finalTotalSales);
      const safeTotalPaid = hasAnyActivity ? finalTotalPaid : (currentCust ? (currentCust as any).total_paid_amount || 0 : finalTotalPaid);
      const safeTotalDebt = hasAnyActivity ? finalTotalDebt : (currentCust ? (currentCust as any).total_debt_amount || 0 : finalTotalDebt);

      // İcra durumu
      const activeEnf = custEnf.filter((e: any) => e.status !== 'Tahsil Edildi' && e.status !== 'Dosya Kapatıldı' && e.status !== 'İptal Edildi');
      const hasActiveEnforcement = activeEnf.length > 0;

      // Müşteri durumu
      let custStatus = 'Aktif';
      if (hasActiveEnforcement) {
        custStatus = 'İcrada';
      } else if (safeTotalDebt <= 0) {
        custStatus = 'Borcu Yok';
      } else {
        // Gecikmiş senet var mı?
        const hasOverdue = custNotes.some((n: any) => n.status === 'Gecikti');
        custStatus = hasOverdue ? 'Gecikmiş' : 'Borçlu';
      }

      // Son alışveriş tarihi
      const lastSale = custSales.sort((a: any, b: any) => (b.sale_date || '').localeCompare(a.sale_date || ''))[0];
      const lastNote = legacyNotes.sort((a: any, b: any) => (b.purchase_date || '').localeCompare(a.purchase_date || ''))[0];
      const lastPurchaseDate = lastSale?.sale_date || lastNote?.purchase_date || null;

      const { error: updErr } = await supabase
        .from('customers')
        .update({
          total_sales_amount: safeTotalSales,
          total_paid_amount: safeTotalPaid,
          total_debt_amount: safeTotalDebt,
          status: custStatus,
          last_purchase_date: lastPurchaseDate,
          updated_at: new Date().toISOString(),
        })
        .eq('id', cid)
        .eq('business_id', bizId);

      if (updErr) {
        errors.push(`${cid}: ${handleError(updErr)}`);
      } else {
        updated++;
      }
    }

    return { data: { updated, errors }, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

// ============================================================
// LEGACY CUSTOMER WITH INSTALLMENT PLAN (artık sadece senet oluşturur)
// ============================================================

export async function createLegacyCustomerWithPlan(payload: {
  customer: any;
  legacySale: {
    purchase_date: string;
    total_amount: number;
    paid_amount: number;
    remaining_amount: number;
    payment_type: string;
    notes: string;
  };
  planType: 'equal' | 'custom';
  equalPlan: import('@/components/feature/LegacyInstallmentPlan').EqualPlanData | null;
  customInstallments: import('@/components/feature/LegacyInstallmentPlan').CustomInstallmentRow[] | null;
  noteData?: {
    note_no: string | null;
    note_book_no: string;
    note_page_no: string;
    note_sequence_no: string;
    issue_date: string;
    due_date: string;
    guarantor_name: string;
    guarantor_phone: string;
    description: string;
  };
}): Promise<DbResult<{ customer: any; note: any | null; payment: any | null }>> {
  try {
    // 1. Create customer
    const { data: createdCustomer, error: custErr } = await createCustomer(payload.customer);
    if (custErr || !createdCustomer) {
      return { data: null, error: custErr || 'Müşteri oluşturulamadı' };
    }
    const customerId = (createdCustomer as any).id;

    const totalAmount = payload.legacySale.total_amount;
    const paidAmount = payload.legacySale.paid_amount;
    const remainingAmount = payload.legacySale.remaining_amount;

    // 2. Create promissory note only (no sale, no installments)
    let createdNote: any = null;
    if (payload.noteData) {
      const { data: autoNoteNo } = await generateNoteNo();
      const finalNoteNo = payload.noteData.note_no || autoNoteNo || `SNT-${new Date().getFullYear()}-001`;

      const today = new Date().toISOString().split('T')[0];
      let noteStatus = 'Bekliyor';
      if (remainingAmount <= 0) noteStatus = 'Ödendi';
      else if (payload.noteData.due_date && payload.noteData.due_date < today) noteStatus = 'Gecikti';

      const { data: noteResult, error: noteErr } = await create('promissory_notes', {
        note_no: finalNoteNo,
        legacy_note_no: payload.noteData.note_no || null,
        customer_id: customerId,
        customer_name: (createdCustomer as any).full_name,
        customer_phone: (createdCustomer as any).phone,
        sale_id: null,
        sale_no: null,
        amount: totalAmount,
        paid_amount: paidAmount,
        remaining_amount: remainingAmount,
        issue_date: (payload.noteData.issue_date || payload.legacySale.purchase_date) || null,
        due_date: payload.noteData.due_date || null,
        note_book_no: payload.noteData.note_book_no,
        note_page_no: payload.noteData.note_page_no,
        note_sequence_no: payload.noteData.note_sequence_no,
        guarantor_name: payload.noteData.guarantor_name,
        guarantor_phone: payload.noteData.guarantor_phone,
        description: payload.noteData.description || 'Eski sistemden devir (taksitli senet)',
        status: noteStatus,
        source_type: 'legacy_opening_balance',
        purchase_date: payload.legacySale.purchase_date,
      });

      if (noteErr) {
        // Rollback: delete customer
        await remove('customers', customerId);
        return { data: null, error: `Senet oluşturulamadı: ${noteErr}` };
      }
      createdNote = noteResult;
    }

    // 3. Create single legacy payment if there were pre-payments
    let createdPayment: any = null;
    if (paidAmount > 0) {
      const { data: legacyReceipt } = await getNextDocumentNumber('LGC');
      const receiptNo = legacyReceipt || `LGCY-${new Date().getFullYear()}-0001`;
      const { data: payment } = await create('payments', {
        customer_id: customerId,
        customer_name: (createdCustomer as any).full_name,
        sale_id: null,
        sale_no: null,
        promissory_note_id: createdNote ? (createdNote as any).id : null,
        payment_date: payload.legacySale.purchase_date,
        amount: paidAmount,
        payment_method: 'Devir Kaydı',
        receipt_no: receiptNo,
        description: 'Eski sistemde daha önce yapılmış toplam ödeme',
        received_by: 'Sistem',
        is_legacy_payment: true,
      });
      if (payment) createdPayment = payment;
    }

    // 4. Update customer totals (based on note, no sale duplication)
    await updateCustomer(customerId, {
      total_sales_amount: 0,
      total_paid_amount: paidAmount,
      total_debt_amount: remainingAmount,
      last_purchase_date: payload.legacySale.purchase_date,
      status: remainingAmount > 0 ? 'Borçlu' : 'Borcu Yok',
    });

    return {
      data: {
        customer: createdCustomer,
        note: createdNote,
        payment: createdPayment,
      },
      error: null,
    };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

// ============================================================
// EXPORT / IMPORT / RESET
// ============================================================

export async function exportAllData(): Promise<DbResult<string>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const tables = ['customers', 'products', 'sales', 'sale_items', 'payments', 'installments', 'deliveries', 'expenses', 'cash_movements', 'stock_movements', 'promissory_notes', 'enforcement_cases', 'customer_contacts'];
    const result: Record<string, any[]> = {};

    for (const table of tables) {
      const { data } = await supabase
        .from(table)
        .select('*')
        .eq('business_id', bizId);
      result[table] = data || [];
    }

    return { data: JSON.stringify(result, null, 2), error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

// ============================================================
// GET PAYMENTS BY MONTH (range query helper)
// ============================================================
export { queryRange as getPaymentsByMonthRange };

export async function getPaymentAllocations(paymentId: string): Promise<DbResult<any[]>> {
  return queryEq('payment_allocations', 'payment_id', paymentId);
}

export async function getInstallmentsByCustomerId(customerId: string): Promise<DbResult<any[]>> {
  return queryEq('installments', 'customer_id', customerId);
}

/**
 * Müşterinin TÜM açık taksitlerini getirir (satış fark etmeksizin).
 * Her taksit için bağlı satış ve senet bilgilerini de döndürür.
 * Sadece remaining_amount > 0 ve status != Ödendi/İptal olanları getirir.
 */
export async function getCustomerOpenInstallments(customerId: string): Promise<DbResult<any[]>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { data, error } = await supabase
      .from('installments')
      .select('*')
      .eq('business_id', bizId)
      .eq('customer_id', customerId)
      .not('status', 'in', '("Ödendi","İptal")')
      .gt('remaining_amount', 0)
      .order('due_date', { ascending: true, nullsFirst: false })
      .order('sequence_no', { ascending: true });

    if (error) return { data: null, error: handleError(error) };

    const installments = (data || []) as any[];

    // Enrich with sale and note info
    if (installments.length > 0) {
      const saleIds = [...new Set(installments.map(i => i.sale_id).filter(Boolean))];
      if (saleIds.length > 0) {
        const [salesRes, notesRes] = await Promise.all([
          supabase.from('sales').select('id, sale_no, is_legacy, payment_type').in('id', saleIds).eq('business_id', bizId),
          supabase.from('promissory_notes').select('id, note_no, sale_id, status, amount, remaining_amount').in('sale_id', saleIds).eq('business_id', bizId).not('status', 'in', '("İptal")'),
        ]);

        const salesMap = new Map((salesRes.data || []).map((s: any) => [s.id, s]));
        const notesMap = new Map<string, any[]>();
        (notesRes.data || []).forEach((n: any) => {
          if (!notesMap.has(n.sale_id)) notesMap.set(n.sale_id, []);
          notesMap.get(n.sale_id)!.push(n);
        });

        for (const inst of installments) {
          const sale = salesMap.get(inst.sale_id);
          if (sale) {
            inst.sale_no = (sale as any).sale_no;
            inst.is_legacy_sale = (sale as any).is_legacy || false;
            inst.sale_payment_type = (sale as any).payment_type;
          }
          const notes = notesMap.get(inst.sale_id) || [];
          inst.linked_note = notes.length > 0 ? notes[0] : null;
          inst.linked_note_no = notes.length > 0 ? notes[0].note_no : null;
        }
      }
    }

    return { data: installments, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

/**
 * Taksit Planı ödemesi - müşterinin tüm açık taksitlerine cross-sale dağıtım yapar.
 * Yeni atomic RPC'yi (record_installment_plan_payment_transaction) çağırır.
 */
export async function recordInstallmentPlanPayment(payload: {
  customer_id: string;
  payment_date: string;
  amount: number;
  payment_method: string;
  description: string;
  received_by: string;
  installment_ids: string[];
  auto_distribute?: boolean;
}): Promise<DbResult<any>> {
  try {
    const { data, error } = await supabase.rpc('record_installment_plan_payment_transaction', {
      payload: payload as any,
    });

    if (error) return { data: null, error: handleError(error) };
    if (data && (data as any).success === false) {
      return { data: null, error: (data as any).error || 'Ödeme kaydedilemedi' };
    }
    return { data, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

export async function recordInstallmentPayment(payload: {
  customer_id: string;
  sale_id: string;
  payment_date: string;
  amount: number;
  payment_method: string;
  description: string;
  received_by: string;
  installment_ids: string[];
}): Promise<DbResult<any>> {
  try {
    const { data, error } = await supabase.rpc('record_installment_payment_transaction', {
      payload: payload as any,
    });
    if (error) return { data: null, error: handleError(error) };
    return { data, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

// ============================================================
// UNIFIED PAYMENT (Yeni atomic ödeme sistemi)
// ============================================================

export interface UnifiedPaymentPayload {
  source_type: 'sale' | 'installment' | 'note' | 'legacy_debt' | 'installment_plan';
  customer_id: string;
  sale_id?: string | null;
  note_id?: string | null;
  amount: number;
  payment_date: string;
  payment_method: string;
  description: string;
  received_by: string;
  installment_ids?: string[];
  auto_distribute?: boolean;
}

export async function recordUnifiedPayment(payload: UnifiedPaymentPayload): Promise<DbResult<any>> {
  try {
    // installment_plan uses its own dedicated RPC
    if (payload.source_type === 'installment_plan') {
      return recordInstallmentPlanPayment({
        customer_id: payload.customer_id,
        payment_date: payload.payment_date,
        amount: payload.amount,
        payment_method: payload.payment_method,
        description: payload.description,
        received_by: payload.received_by,
        installment_ids: payload.installment_ids || [],
        auto_distribute: payload.auto_distribute || false,
      });
    }

    const rpcPayload: Record<string, any> = {
      source_type: payload.source_type,
      customer_id: payload.customer_id,
      amount: payload.amount,
      payment_date: payload.payment_date,
      payment_method: payload.payment_method,
      description: payload.description,
      received_by: payload.received_by,
    };

    if (payload.sale_id) rpcPayload.sale_id = payload.sale_id;
    if (payload.note_id) rpcPayload.note_id = payload.note_id;
    if (payload.installment_ids && payload.installment_ids.length > 0) {
      rpcPayload.installment_ids = payload.installment_ids;
    }

    const { data, error } = await supabase.rpc('record_unified_payment_transaction', {
      payload: rpcPayload,
    });

    if (error) return { data: null, error: handleError(error) };
    if (data && (data as any).success === false) {
      return { data: null, error: (data as any).error || 'Ödeme kaydedilemedi' };
    }
    return { data, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

/**
 * Ödeme alındıktan sonra, ilgili müşterinin açık ödeme sözlerini
 * otomatik olarak senkronize eder. FIFO (en eski söz önce) dağıtım yapar.
 */
export async function syncPaymentPromisesAfterPayment(
  paymentId: string,
  customerId: string,
  amount: number
): Promise<DbResult<any>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { data, error } = await supabase.rpc('sync_payment_promises', {
      p_payment_id: paymentId,
      p_business_id: bizId,
      p_customer_id: customerId,
      p_amount: amount,
    });

    if (error) return { data: null, error: handleError(error) };
    return { data: data as any, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

export async function cancelPaymentTransaction(paymentId: string): Promise<DbResult<any>> {
  try {
    const { data, error } = await supabase.rpc('cancel_payment_transaction', {
      payment_uuid: paymentId,
    });
    if (error) return { data: null, error: handleError(error) };
    return { data, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

// ============================================================
// BULK / UTILITY
// ============================================================

export async function getRecordCounts(): Promise<DbResult<Record<string, number>>> {
  const tables = ['customers', 'products', 'sales', 'sale_items', 'payments', 'installments', 'deliveries', 'expenses', 'cash_movements', 'stock_movements', 'promissory_notes'];
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const counts: Record<string, number> = {};
    for (const table of tables) {
      const { count, error } = await supabase
        .from(table)
        .select('*', { count: 'exact', head: true })
        .eq('business_id', bizId);

      if (error) {
        counts[table] = -1;
      } else {
        counts[table] = count || 0;
      }
    }
    return { data: counts, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

export async function importRecord(table: string, record: any): Promise<DbResult<any>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const clean = sanitizeItem(record);
    clean.business_id = bizId;

    const { data, error } = await supabase
      .from(table)
      .upsert(clean, { onConflict: 'id', ignoreDuplicates: false })
      .select()
      .single();

    if (error) return { data: null, error: handleError(error) };
    return { data, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

export async function importRecords(table: string, records: any[]): Promise<DbResult<{ imported: number; total: number; errors: string[] }>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    if (!records || records.length === 0) {
      return { data: { imported: 0, total: 0, errors: [] }, error: null };
    }

    const errors: string[] = [];
    let successCount = 0;

    const cleaned = records.map(r => {
      const clean = sanitizeItem(r);
      clean.business_id = bizId;
      // Remove columns that don't exist in the table (safe-guard)
      delete clean.principal_amount;
      return clean;
    });

    // Step 1: Try batch upsert for speed
    const { error: batchError } = await supabase
      .from(table)
      .upsert(cleaned, { onConflict: 'id', ignoreDuplicates: false });

    if (!batchError) {
      return { data: { imported: records.length, total: records.length, errors: [] }, error: null };
    }

    // Step 2: Batch failed — fall back to one-at-a-time and track failures
    for (let i = 0; i < cleaned.length; i++) {
      const { error: singleError } = await supabase
        .from(table)
        .upsert(cleaned[i], { onConflict: 'id', ignoreDuplicates: false });

      if (singleError) {
        errors.push(`Kayıt ${i + 1}: ${handleError(singleError)}`);
      } else {
        successCount++;
      }
    }

    return { data: { imported: successCount, total: records.length, errors }, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

export async function resetAllData(): Promise<DbResult<boolean>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    // Silme sırası önemli: foreign key bağımlılığı olan child tablolar önce silinmeli
    const tables = [
      'sale_items',
      'installments',
      'stock_movements',
      'cash_movements',
      'customer_contacts',
      'payment_promise_activities',
      'payment_promises',
      'enforcement_cases',
      'promissory_notes',
      'payments',
      'deliveries',
      'expenses',
      'sales',
      'products',
      'customers',
    ];

    const errors: string[] = [];

    for (const table of tables) {
      const { error } = await supabase
        .from(table)
        .delete()
        .eq('business_id', bizId);

      if (error) {
        errors.push(`${table}: ${handleError(error)}`);
      }
    }

    if (errors.length > 0) {
      return { data: false, error: errors.join(' | ') };
    }

    return { data: true, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

// ============================================================
// FINANCIAL REPORTS (RPC-based)
// ============================================================

export interface FinancialReportSummary {
  total_revenue: number;
  sales_revenue: number;
  independent_note_revenue: number;
  total_cost: number;
  gross_profit: number;
  certain_gross_profit: number;
  total_expenses: number;
  net_profit: number;
  collected_amount: number;
  outstanding_amount: number;
  unknown_cost_revenue: number;
  known_cost_revenue: number;
  sales_count: number;
  known_cost_sales_count: number;
  unknown_cost_sales_count: number;
  profitable_sales_count: number;
  loss_sales_count: number;
}

export interface ProfitDetailItem {
  sale_id: string;
  sale_no: string;
  sale_date: string;
  customer_name: string;
  sale_total: number;
  payment_type: string;
  employee_id: string | null;
  created_by: string | null;
  item_id: string;
  product_name: string;
  product_type: string;
  brand: string;
  model_code: string;
  quantity: number;
  unit_sale_price: number;
  unit_cost_price: number | null;
  item_revenue: number;
  item_cost: number | null;
  item_profit: number | null;
  profit_rate: number | null;
}

export interface ProfitDetails {
  items: ProfitDetailItem[];
  total_count: number;
}

export async function getFinancialReportSummary(
  startDate: string,
  endDate: string
): Promise<DbResult<FinancialReportSummary>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { data, error } = await supabase.rpc('get_financial_report_summary', {
      p_business_id: bizId,
      p_start_date: startDate,
      p_end_date: endDate,
    });

    if (error) return { data: null, error: handleError(error) };
    return { data: data as FinancialReportSummary, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

export async function getProfitDetails(
  startDate: string,
  endDate: string
): Promise<DbResult<ProfitDetails>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { data, error } = await supabase.rpc('get_profit_details', {
      p_business_id: bizId,
      p_start_date: startDate,
      p_end_date: endDate,
    });

    if (error) return { data: null, error: handleError(error) };
    return { data: data as ProfitDetails, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

// ============================================================
// DEBT ADJUSTMENTS (Borç Düzeltme)
// ============================================================

export async function adjustCustomerDebt(payload: {
  customer_id: string;
  new_total_debt: number;
  reason: string;
  description?: string;
  processed_by: string;
  processed_by_name: string;
  linked_sale_id?: string | null;
  linked_note_id?: string | null;
}): Promise<DbResult<any>> {
  try {
    const { data, error } = await supabase.rpc('adjust_customer_debt', {
      p_customer_id: payload.customer_id,
      p_new_total_debt: payload.new_total_debt,
      p_reason: payload.reason,
      p_description: payload.description || '',
      p_processed_by: payload.processed_by,
      p_processed_by_name: payload.processed_by_name,
      p_linked_sale_id: payload.linked_sale_id || null,
      p_linked_note_id: payload.linked_note_id || null,
    });
    if (error) return { data: null, error: handleError(error) };
    if (data && (data as any).success === false) return { data: null, error: (data as any).error || 'Düzeltme başarısız.' };
    return { data: data as any, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

export async function getDebtAdjustmentsByCustomerId(customerId: string): Promise<DbResult<any[]>> {
  return queryEq('debt_adjustments', 'customer_id', customerId);
}

// ============================================================
// DASHBOARD RPC — Tek veri kaynağı (Summary + Detail)
// ============================================================

export interface DashboardSummary {
  today_revenue: number;
  total_customers: number;
  total_receivables: number;
  today_collections: number;
  in_stock_products: number;
  today_deliveries: number;
  pending_notes_count: number;
  pending_notes_total: number;
  overdue_notes_count: number;
  overdue_notes_total: number;
  active_enforcement_count: number;
  enforcement_total_debt: number;
  active_employees: number;
  on_leave_employees: number;
  month_salary_paid: number;
  pending_salary: number;
  month_net_profit: number;
  month_total_revenue: number;
  month_total_cost: number;
  month_total_expenses: number;
  month_gross_profit: number;
  month_collected: number;
  month_sales_count: number;
}

export interface DashboardDetail {
  card_type: string;
  rows: any[];
  total: number;
  count?: number;
}

export async function getDashboardSummary(): Promise<DbResult<DashboardSummary>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { data, error } = await supabase.rpc('get_dashboard_summary', {
      p_business_id: bizId,
    });

    if (error) return { data: null, error: handleError(error) };
    return { data: data as DashboardSummary, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

export async function getDashboardDetail(
  cardType: string,
  startDate?: string,
  endDate?: string
): Promise<DbResult<DashboardDetail>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { data, error } = await supabase.rpc('get_dashboard_detail', {
      p_business_id: bizId,
      p_card_type: cardType,
      p_start_date: startDate || null,
      p_end_date: endDate || null,
    });

    if (error) return { data: null, error: handleError(error) };
    return { data: data as DashboardDetail, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

// ============================================================
// CUSTOMER SUMMARY REPORT (RPC-based)
// ============================================================

export interface CustomerSummaryReport {
  customer: any;
  financial_summary: {
    total_alısveris: number;
    total_paid: number;
    current_debt: number;
    overdue_debt: number;
    open_notes_total: number;
    open_installments_total: number;
    enforcement_debt: number;
    last_payment_date: string | null;
    last_purchase_date: string | null;
  };
  sales: any[];
  sale_items: any[];
  installments: any[];
  promissory_notes: any[];
  payments: any[];
  payment_promises: any[];
  deliveries: any[];
  enforcement_cases: any[];
}

export async function getCustomerSummaryReport(
  customerId: string,
  startDate?: string | null,
  endDate?: string | null
): Promise<DbResult<CustomerSummaryReport>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { data, error } = await supabase.rpc('get_customer_summary_report', {
      p_customer_id: customerId,
      p_business_id: bizId,
      p_start_date: startDate || null,
      p_end_date: endDate || null,
    });

    if (error) return { data: null, error: handleError(error) };

    const result = data as any;
    if (result?.error) return { data: null, error: result.error };

    return { data: result as CustomerSummaryReport, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

// ============================================================
// MERKEZİ TOPLAM ALACAK HESAPLAMA (RPC-based)
// ============================================================

export interface OutstandingReceivable {
  total_receivable: number;
  sales_receivable: number;
  independent_notes_receivable: number;
  sale_count: number;
  independent_note_count: number;
}

// ============================================================
// MERKEZİ SATIŞ ÖZETİ (RPC-based)
// ============================================================

export interface SalesSummary {
  total_revenue: number;
  total_collected: number;
  total_remaining: number;
  total_cost: number;
  gross_profit: number;
  gross_margin: number;
  sales_count: number;
  active_count: number;
  missing_cost_items: number;
}

export async function getSalesSummary(): Promise<DbResult<SalesSummary>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { data, error } = await supabase.rpc('get_sales_summary', {
      p_business_id: bizId,
    });

    if (error) return { data: null, error: handleError(error) };
    return { data: data as SalesSummary, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

// ============================================================
// MERKEZİ KASA ÖZETİ (RPC-based)
// ============================================================

export interface CashSummary {
  total_income: number;
  total_expense: number;
  balance: number;
  today_income: number;
  today_expense: number;
  today_net: number;
}

export async function getCashSummary(): Promise<DbResult<CashSummary>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { data, error } = await supabase.rpc('get_cash_summary', {
      p_business_id: bizId,
    });

    if (error) return { data: null, error: handleError(error) };
    return { data: data as CashSummary, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

// ============================================================
// MERKEZİ GİDER ÖZETİ (RPC-based)
// ============================================================

export interface ExpenseSummary {
  total_paid: number;
  total_pending: number;
  total_amount: number;
  count: number;
}

export async function getExpenseSummary(): Promise<DbResult<ExpenseSummary>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { data, error } = await supabase.rpc('get_expense_summary', {
      p_business_id: bizId,
    });

    if (error) return { data: null, error: handleError(error) };
    return { data: data as ExpenseSummary, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

// ============================================================
// MERKEZİ SENET ÖZETİ (RPC-based)
// ============================================================

export interface PromissoryNoteSummary {
  total_amount: number;
  total_remaining: number;
  pending_count: number;
  pending_total: number;
  overdue_count: number;
  overdue_total: number;
  paid_count: number;
  cancelled_count: number;
}

export async function getPromissoryNoteSummary(): Promise<DbResult<PromissoryNoteSummary>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { data, error } = await supabase.rpc('get_promissory_note_summary', {
      p_business_id: bizId,
    });

    if (error) return { data: null, error: handleError(error) };
    return { data: data as PromissoryNoteSummary, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

export async function getTotalOutstandingReceivable(): Promise<DbResult<OutstandingReceivable>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { data, error } = await supabase.rpc('get_total_outstanding_receivable', {
      p_business_id: bizId,
    });

    if (error) return { data: null, error: handleError(error) };
    return { data: data as OutstandingReceivable, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

// ============================================================
// UPDATE SALE TRANSACTION (RPC-based, transaction-safe)
// ============================================================

export interface UpdateSaleTransactionPayload {
  sale_id: string;
  sale_data: Record<string, any>;
  sale_items: Record<string, any>[];
  reason: string;
  installment_strategy?: 'last_installment' | 'distribute_evenly' | 'recreate';
  delivery_data?: Record<string, any> | null;
  changed_by?: string;
}

export interface UpdateSaleTransactionResult {
  success: boolean;
  sale_id?: string;
  sale_no?: string;
  old_total_amount?: number;
  new_total_amount?: number;
  old_remaining_amount?: number;
  new_remaining_amount?: number;
  debt_delta?: number;
  cost_delta?: number;
  old_total_cost?: number;
  new_total_cost?: number;
  cost_only_change?: boolean;
  price_changed?: boolean;
  valid_collected?: number;
  revision_no?: number;
  error?: string;
  detail?: any;
  phase_completed?: string;
}

export async function updateSaleTransaction(
  payload: UpdateSaleTransactionPayload
): Promise<DbResult<UpdateSaleTransactionResult>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { data, error } = await supabase.rpc('update_sale_transaction', {
      p_sale_id: payload.sale_id,
      p_sale_data: payload.sale_data,
      p_sale_items: payload.sale_items,
      p_reason: payload.reason,
      p_installment_strategy: payload.installment_strategy || 'last_installment',
      p_delivery_data: payload.delivery_data || null,
      p_changed_by: payload.changed_by || null,
    });

    if (error) return { data: null, error: handleError(error) };

    const result = data as UpdateSaleTransactionResult;
    if (!result || result.success === false) {
      return { data: null, error: (result && result.error) || 'Satış güncellenemedi' };
    }

    return { data: result, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

// ============================================================
// MERKEZİ MÜŞTERİ FİNANSAL ÖZET (RPC-based)
// ============================================================

export interface CustomerFinancialSummary {
  total_sales_count: number;
  total_purchase_amount: number;
  total_collected: number;
  active_sales_remaining: number;
  independent_notes_remaining: number;
  legacy_remaining: number;
  total_remaining_debt: number;
  last_purchase_date: string | null;
  last_note_info: {
    note_no: string;
    note_book_no: string;
    note_page_no: string;
    note_sequence_no: string;
    remaining_amount: number;
    status: string;
    due_date: string;
  } | null;
  enforcement_debt: number;
  has_overdue: boolean;
  has_active_enforcement: boolean;
  suggested_status: string;
}

export async function getCustomerFinancialSummary(customerId: string): Promise<DbResult<CustomerFinancialSummary>> {
  try {
    const { data, error } = await supabase.rpc('get_customer_financial_summary', {
      p_customer_id: customerId,
    });

    if (error) return { data: null, error: handleError(error) };

    const result = data as any;
    if (result?.error) return { data: null, error: result.error };

    return { data: result as CustomerFinancialSummary, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

// ============================================================
// NEW: CENTRAL PAYMENT PROMISE INTEGRATION RPCs
// ============================================================

/**
 * Müşterinin güncel borcunu bileşenlerine ayırarak RPC'den alır.
 * Ödeme sözü, teslimat, kişisel hesap gibi kaynakları dahil ETMEZ.
 */
export async function getCustomerDebtComponents(customerId: string): Promise<DbResult<any>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { data, error } = await supabase.rpc('get_customer_debt_components', {
      p_customer_id: customerId,
      p_business_id: bizId,
    });

    if (error) return { data: null, error: handleError(error) };
    return { data: data as any, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

/**
 * Tüm ödeme sözlerini güncel müşteri borcu ve kaynak kalanı ile birlikte getirir.
 * Eski `getPaymentPromises() + customerMap` yerine bunu kullanın.
 */
export async function getPaymentPromisesWithCurrentDebt(
  filterStatuses?: string[] | null
): Promise<DbResult<any[]>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { data, error } = await supabase.rpc('get_payment_promises_with_current_debt', {
      p_business_id: bizId,
      p_filter_statuses: filterStatuses || null,
    });

    if (error) return { data: null, error: handleError(error) };

    const result = data as any;
    if (result?.success && result?.data) {
      return { data: result.data as any[], error: null };
    }
    return { data: [], error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}

/**
 * Bir müşterinin tüm açık ödeme sözlerini yeniden hesaplatır.
 * Ödeme, iptal, satış düzenleme sonrası çağrılmalıdır.
 */
export async function recalculatePaymentPromisesForCustomer(
  customerId: string
): Promise<DbResult<any>> {
  try {
    const bizId = await getUserBusinessId();
    if (!bizId) return { data: null, error: 'İşletme bilgisi bulunamadı' };

    const { data, error } = await supabase.rpc('recalculate_payment_promises_for_customer', {
      p_customer_id: customerId,
      p_business_id: bizId,
    });

    if (error) return { data: null, error: handleError(error) };
    return { data: data as any, error: null };
  } catch (err: any) {
    return { data: null, error: handleError(err) };
  }
}